/*
 * Author: Axel Antoine
 * mail: ax.antoine@gmail.com
 * website: http://axantoine.com
 * Created on Tue Oct 25 2022
 *
 * Loki, Inria project-team with Université de Lille
 * within the Joint Research Unit UMR 9189
 * CNRS - Centrale Lille - Université de Lille, CRIStAL
 * https://loki.lille.inria.fr
 *
 * Licence: Licence.md
 */

import { Vector3 } from 'three';
import { Halfedge } from '../core/Halfedge';
import { HalfedgeDS } from '../core/HalfedgeDS';
import { Vertex } from '../core/Vertex';

export function splitEdge(struct: HalfedgeDS, he: Halfedge, position: Vector3, tolerance = 1e-10) {
  const twin = he.twin;
  const A = he.vertex;
  const B = twin.vertex;

  if (A.matchesPosition(position, tolerance)) return A;
  if (B.matchesPosition(position, tolerance)) return B;

  const V = new Vertex(struct.vertices.length);
  V.position.copy(position);

  // The edge pair A↔B becomes two pairs: A↔V (kept on `he`) and V↔B (kept on
  // `twin`). Two new halfedges carry the other direction of each pair. The
  // same surgery holds when the twin side is a border (face === null): the
  // border next-cycle threads through twin_new exactly like a face loop.
  const he_new = new Halfedge(V); // V -> B, continues he's side
  const twin_new = new Halfedge(V); // V -> A, continues twin's side

  // he: A -> V, twin_new: V -> A
  he.twin = twin_new;
  twin_new.twin = he;
  // he_new: V -> B, twin: B -> V
  he_new.twin = twin;
  twin.twin = he_new;

  he_new.face = he.face;
  twin_new.face = twin.face;

  // he's side: A -> V -> B -> (original he.next chain)
  const he_next_old = he.next;
  he.next = he_new;
  he_new.prev = he;
  he_new.next = he_next_old;
  he_next_old.prev = he_new;

  // twin's side: B -> V -> A -> (original twin.next chain)
  const twin_next_old = twin.next;
  twin.next = twin_new;
  twin_new.prev = twin;
  twin_new.next = twin_next_old;
  twin_next_old.prev = twin_new;

  // A's and B's outgoing halfedges (`he`, `twin`) both survive with their
  // vertex unchanged, so their pointers stay valid; only V needs one.
  V.halfedge = he_new;

  struct.vertices.push(V);
  struct.halfedges.push(he_new, twin_new);
  struct.idToVertex.set(V.id, V);
  struct.renderIdToVertex.set(V.renderId, V);
  struct.vertexToRenderId.set(V, V.renderId);

  return V;
}
